import { AiOutlineArrowUp, AiOutlineArrowDown } from "react-icons/ai";
import React, { useMemo, useState, useCallback, useEffect } from "react";
import { RoleData } from "@/types";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
  SortingState,
} from "@tanstack/react-table";
import { UserData, UpdateUser } from "@/types/user";
import SelectField from "../inputFields/SelectField";
import styles from "./css/UsersTable.module.css";
import { UserModal } from "../modals/UserModal";
import { useSelector, useDispatch } from "react-redux";
import { truncateText } from "@/utils/truncateText";
import { UserAvatar } from "@/components/addons/UserAvatar";
import { RootState } from "@/store/store";
import { useUpdateUser } from "@/hooks/user/useUpdateUser";
import { setToast } from "@/store/systemSlice";
import { useFetchAllUsers } from "@/utils/fetchData";
import { useTranslation } from 'react-i18next';
import { getEntityIdPair } from "@/utils/getEntityPairId";


interface UsersTableProps {
  className?: string;
  data: UserData[];
  users: UserData[];
}

const UsersTable: React.FC<UsersTableProps> = ({ className, data, users }) => {
  const { t } = useTranslation('UsersTable');
  const { t: tRoles } = useTranslation('RolesNames');
  const [selectedUser, setSelectedUser] = useState<UserData>();
  const [showModal, setShowModal] = useState(false);
  const [localData, setLocalData] = useState<UserData[]>(data);
  const user = useSelector((state: RootState) => state.user.user);
  const roles = useSelector((state: RootState) => state.systemService.roles).filter((role) => user.user_type === "admin" ? role.name !== "super_admin" && role.name !== "external_municipal" : role.name === "external_municipal");
  const city = useSelector((state: RootState) => state.entity.userCity);
  const ao = useSelector((state: RootState) => state.entity.userAo);
  const entityType = useSelector((state: RootState) => state.entity.entityType);
  const userId = useSelector((state: RootState) => state.user.user.id);
  const dispatch = useDispatch();
  const getAllUsers = useFetchAllUsers();
  const [updatingUserId, setUpdatingUserId] = useState<string>("");
  

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const updateUserMutation = useUpdateUser({
    onSuccess: (data) => {
      getAllUsers.mutate({ ...getEntityIdPair(entityType, city, ao), });
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: data.message,
          toastType: "success",
        })
      );
    },
    onError: (error) => {
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: error.response.data.message,
          toastType: "error",
        })
      );
    },
  });

  const columns = useMemo<ColumnDef<UserData>[]>(
    () => [
      {
        accessorKey: "color",
        header: "",
        cell: ({ row }) => {
          const color = row.original.color;
          const image = row.original.user_image;
          return <UserAvatar color={color || "blue"} userImage={image} />;
        },
      },
      {
        accessorKey: "user_name",
        header: t("userName"),
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return (
            <div className={styles.userNameColumn}>
              {truncateText(value, 26)}
            </div>
          );
        },
      },
      {
        accessorKey: "role_id",
        header: t("userRole"),
        cell: ({ getValue, row }) => {
          const value = getValue<string>();
          const role = roles.find((role) => role.id === value);
          const translatedRole = role ? {
            ...role,
            name: tRoles(role.name || "")
          } : undefined;
          const isCurrentUser = row.original.id === userId;

          const handleUpdate = (selectedRole: RoleData | undefined) => {
            if (updateUserMutation.isPending) return;
            
            if (selectedRole?.id === value) return;

            try {
              setUpdatingUserId(row.original.id);
              const updatedUser: UpdateUser = {
                ...getEntityIdPair(entityType, city, ao),
                user_id: row.original.id,
                role_id: selectedRole ? selectedRole.id : "",
              };
              updateUserMutation.mutate(updatedUser);

              setLocalData((prevData) =>
                prevData.map((user) =>
                  user.id === row.original.id
                    ? { ...user, role_id: selectedRole ? selectedRole.id : "" }
                    : user
                )
              );
            } catch (error) {
              setUpdatingUserId("");
              console.error("Update failed");
            }
          };

          return (
            <div
              className={styles.roleColumn}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2">
                <SelectField
                  size="small"
                  data={roles.map(role => ({
                    ...role,
                    name: tRoles(role.name)
                  }))}
                  updateFunction={handleUpdate}
                  selectedValue={translatedRole as RoleData}
                  showEmpty={false}
                  disabledState={isCurrentUser || updateUserMutation.isPending}
                />
                {/* spinner animation while updating user role */}
                {updateUserMutation.isPending && updatingUserId === row.original.id && (
                  <div className="ml-2 animate-spin h-4 w-4">
                    <svg className="text-blue-600" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  </div>
                )}
                {/* ------------------------------------------------ */}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "email",
        header: t("email"),
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return (
            <div className={styles.emailColumn}>{truncateText(value, 30)}</div>
          );
        },
      },
      {
        accessorKey: "created_by",
        header: t("createdBy"),
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return (
            <div className={styles.createdByColumn}>
              {truncateText(value, 14)}
            </div>
          );
        },
      },
    ],
    [roles, updateUserMutation]
  );

  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data: localData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleRowClick = useCallback((row: any) => {
    setSelectedUser(row.original);
    setShowModal(true);
  }, []);

  return (
    <>
      <div className={`${styles.tableContainer} ${className}`}>
        <table className={styles.table}>
          <thead className={styles.tableHeadersRow}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className={`${styles.headerText} ${
                      styles[header.id + "Column"]
                    }`}
                    onClick={
                      header.id !== "color"
                        ? header.column.getToggleSortingHandler()
                        : () => {}
                    }
                  >
                    <div className="flex items-center">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.id !== "color" && (
                        <span className="ml-2">
                          {{
                            asc: <AiOutlineArrowUp className="text-black" />,
                            desc: <AiOutlineArrowDown className="text-black" />,
                            undefined: (
                              <AiOutlineArrowUp className="text-black" />
                            ),
                          }[header.column.getIsSorted() as string] ?? (
                            <AiOutlineArrowUp className="text-black" />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className={styles.bodyRow}>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={styles.rowHover}
                onClick={() => handleRowClick(row)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`${styles.rowText} ${
                      cell.column.id === "project_name"
                        ? styles.projectName
                        : ""
                    }`}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showModal && (
        <UserModal
          users={users}
          user={selectedUser as UserData}
          setUser={() => {}}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export default React.memo(UsersTable, (prevProps, nextProps) => {
  return (
    JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) &&
    prevProps.className === nextProps.className
  );
});
